import { useState } from 'react';
import { helpInfo } from './info';

import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalRoot,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@pella/sash-framework';

import './styles.css';
import styles from './Help.module.css';

const AnyTooltipTrigger = /** @type {import('react').ElementType} */ (TooltipTrigger);
const AnyModalHeader = /** @type {import('react').ElementType} */ (ModalHeader);

/**
 * Help component that provides contextual help with multi-step dialogs.
 * Displays help content with navigation between steps and access control.
 * @param {object} props - Component props
 * @param {string} props.item - The help item key to display
 * @param {object} props.access - Access control object for filtering help steps
 * @param {string} props.tip - Tooltip text to display on the help icon
 * @param {string} [props.width] - Dialog width (default: 'md')
 * @returns {import("react").ReactElement} Help component with icon and dialog
 */
const Help = (props) => {
  const { item, access, tip } = props;

  const [open, setOpen] = useState(false);
  const [helpItem, setHelpItem] = useState(null);
  const [step, setStep] = useState(null);
  const [animation, setAnimation] = useState('');

  /**
   * Handle help icon click to open help dialog
   * Filters help content based on access permissions
   * @returns {void}
   */
  const handleClick = () => {
    const foundItem = helpInfo.find((i) => i.item === item);
    if (!foundItem) return;
    const filteredSteps = foundItem.steps.filter((i) => !i.access || access[i.access]);
    const tempItem = { ...foundItem, steps: filteredSteps };
    setHelpItem(tempItem);
    setStep(tempItem?.steps?.length > 0 ? 0 : null);
    setOpen(true);
  };

  /**
   * Handle navigation between help steps with animation
   * @param {string} action - Navigation action ('next' or 'back')
   * @returns {void}
   */
  const stepChange = (action) => {
    setAnimation('');

    let newStep;
    if (action === 'next') {
      newStep = step === helpItem.steps.length - 1 ? 0 : step + 1;
    } else {
      newStep = step === 0 ? helpItem.steps.length - 1 : step - 1;
    }

    setTimeout(() => {
      setStep(newStep);
      setAnimation(action === 'next' ? 'help-in-from-right' : 'help-in-from-left');
    }, 10);
  };

  /**
   * Close the help dialog
   * @returns {void}
   */
  const closeHelp = () => {
    setAnimation('');
    setOpen(false);
  };

  return (
    <>
      <span className={styles.iconWrapper}>
        <Tooltip>
          <AnyTooltipTrigger asChild>
            <Button className='help-icon' iconLeft='help' onClick={handleClick} />
          </AnyTooltipTrigger>
          <TooltipContent>{tip}</TooltipContent>
        </Tooltip>
      </span>
      <ModalRoot open={open} onOpenChange={closeHelp}>
        <Modal showClose={false}>
          <AnyModalHeader className={styles.noSelect}>
            {helpItem?.steps[step]?.title}
            <span className={styles.closeButton}>
              <Button className='help-icon' onClick={closeHelp} iconLeft='close' />
            </span>
          </AnyModalHeader>
          <ModalContent className={styles.noSelectOverflow}>
            <div className={animation}>
              <div>
                {helpItem?.steps[step]?.text?.split('\n').map((line, i, arr) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                  </span>
                ))}
              </div>
              {helpItem?.steps[step]?.img && (
                <div className='help-step-img'>
                  <img src={helpItem?.steps[step]?.img} alt='broken img' />
                </div>
              )}
            </div>
          </ModalContent>
          <ModalFooter>
            {helpItem?.steps?.length > 1 && (
              <>
                <span className={styles.stepCounter}>
                  {step + 1} / {helpItem?.steps?.length}
                </span>
                <Button className='help-icon' onClick={() => stepChange('back')} iconLeft='arrow_back_ios' />
                <Button className='help-icon' onClick={() => stepChange('next')} iconLeft='arrow_forward_ios' />
              </>
            )}
          </ModalFooter>
        </Modal>
      </ModalRoot>
    </>
  );
};

export default Help;
